import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  BacklogSuggestionDoc,
  SuggestionCandidate,
  SuggestionKind,
  SuggestionStatus,
} from '@mct/shared';
import { getFirebaseApp } from './firebase';

export type SuggestionFilter =
  | 'pending'
  | 'snoozed'
  | 'all'
  | 'terminal';

export const FILTER_LABEL: Record<SuggestionFilter, string> = {
  pending: '未処理',
  snoozed: 'あとで',
  all: 'すべて',
  terminal: '終了分',
};

/**
 * Treat a `snoozed` row whose `snoozedUntil` is in the past as effectively
 * `pending`. The server-side expire job runs every 15 min; this mirrors the
 * lazy-expire behavior documented in design-suggestion-inbox.md §4.4.
 */
export function effectiveStatus(
  doc: Pick<BacklogSuggestionDoc, 'status' | 'snoozedUntil'>,
  now: number = Date.now(),
): SuggestionStatus {
  if (doc.status === 'snoozed' && doc.snoozedUntil) {
    if (doc.snoozedUntil.toMillis() <= now) return 'pending';
  }
  return doc.status;
}

export function filterSuggestions(
  rows: readonly BacklogSuggestionDoc[],
  filter: SuggestionFilter,
  now: number = Date.now(),
): BacklogSuggestionDoc[] {
  switch (filter) {
    case 'pending':
      return rows.filter((r) => effectiveStatus(r, now) === 'pending');
    case 'snoozed':
      return rows.filter((r) => effectiveStatus(r, now) === 'snoozed');
    case 'terminal':
      return rows.filter(
        (r) => r.status === 'accepted' || r.status === 'dismissed',
      );
    case 'all':
      return [...rows];
  }
}

export function countPending(
  rows: readonly BacklogSuggestionDoc[],
  now: number = Date.now(),
): number {
  let n = 0;
  for (const r of rows) if (effectiveStatus(r, now) === 'pending') n += 1;
  return n;
}

export function isActionable(
  filter: SuggestionFilter,
  doc: BacklogSuggestionDoc,
  now: number = Date.now(),
): boolean {
  const s = effectiveStatus(doc, now);
  if (filter === 'terminal') return false;
  return s === 'pending' || s === 'snoozed';
}

/**
 * Search-result variant of `effectiveStatus` for realtime inbox rows.
 * `BacklogSearchResultItem`s carry `actionedState` instead of the
 * doc-level `status`, plus an `actionedStateMeta` that surfaces the
 * snooze-expiry timestamp as an ISO string.
 */
export function effectiveActionedState(
  item: {
    actionedState: SuggestionStatus;
    actionedStateMeta: { snoozedUntil: string | null } | null;
  },
  now: number = Date.now(),
): SuggestionStatus {
  if (item.actionedState === 'snoozed' && item.actionedStateMeta?.snoozedUntil) {
    const ms = Date.parse(item.actionedStateMeta.snoozedUntil);
    if (Number.isFinite(ms) && ms <= now) return 'pending';
  }
  return item.actionedState;
}

/**
 * Apply the inbox filter-tab (pending / snoozed / all / terminal) to a
 * realtime-search result list. Mirrors `filterSuggestions` but reads
 * `actionedState` instead of `status`.
 */
export function filterSearchResults<
  T extends {
    actionedState: SuggestionStatus;
    actionedStateMeta: { snoozedUntil: string | null } | null;
  },
>(items: readonly T[], filter: SuggestionFilter, now: number = Date.now()): T[] {
  switch (filter) {
    case 'pending':
      return items.filter((r) => effectiveActionedState(r, now) === 'pending');
    case 'snoozed':
      return items.filter((r) => effectiveActionedState(r, now) === 'snoozed');
    case 'terminal':
      return items.filter(
        (r) => r.actionedState === 'accepted' || r.actionedState === 'dismissed',
      );
    case 'all':
      return [...items];
  }
}

/** URL state sync ------------------------------------------------------- */

const VALID_FILTERS: readonly SuggestionFilter[] = [
  'pending',
  'snoozed',
  'all',
  'terminal',
];

/** Updated-since presets the inbox renders. Wider windows are slower
 *  in Backlog; 90d is a tested-good default. */
export type UpdatedSincePreset = 'all' | '30d' | '90d' | '180d';
export const UPDATED_SINCE_PRESETS: readonly UpdatedSincePreset[] = [
  'all',
  '30d',
  '90d',
  '180d',
];
export const UPDATED_SINCE_LABEL: Record<UpdatedSincePreset, string> = {
  all: '全期間',
  '30d': '30日',
  '90d': '90日',
  '180d': '180日',
};

/**
 * Resolve a preset to an ISO `updatedSince` timestamp, or null when
 * "all" is selected. Pinned to start-of-UTC-day so cache keys are
 * stable within a calendar day (Backlog itself truncates to YYYY-MM-DD).
 */
export function updatedSincePresetToIso(
  preset: UpdatedSincePreset,
  now: number = Date.now(),
): string | null {
  if (preset === 'all') return null;
  const days = preset === '30d' ? 30 : preset === '90d' ? 90 : 180;
  const d = new Date(now);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

export interface InboxUrlState {
  filter: SuggestionFilter;
  wbsItemId: string | null;
  /** Filter chips ↓ (realtime search). */
  statusIds: number[];
  assigneeIds: number[];
  categoryIds: number[];
  milestoneIds: number[];
  priorityIds: number[];
  /**
   * Selected Backlog project keys. Empty = "all configured keys". Drives
   * both the search-side filter and the cascade on the meta callable.
   */
  backlogProjectKeys: string[];
  keyword: string;
  updatedSince: UpdatedSincePreset;
}

function parseIntList(s: string | null): number[] {
  if (!s) return [];
  return s
    .split(',')
    .map((t) => Number(t.trim()))
    .filter((n) => Number.isFinite(n));
}

function encodeIntList(arr: readonly number[]): string {
  return arr.filter((n) => Number.isFinite(n)).join(',');
}

function parseStringList(s: string | null): string[] {
  if (!s) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of s.split(',')) {
    const t = raw.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function encodeStringList(arr: readonly string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of arr) {
    const t = raw.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out.join(',');
}

export function encodeInboxToParams(
  state: InboxUrlState,
  base?: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  for (const k of [
    'view',
    'wbs',
    'st',
    'asg',
    'cat',
    'ms',
    'pr',
    'bp',
    'q',
    'us',
  ]) {
    out.delete(k);
  }
  if (state.filter !== 'pending') out.set('view', state.filter);
  if (state.wbsItemId) out.set('wbs', state.wbsItemId);
  if (state.statusIds.length > 0) out.set('st', encodeIntList(state.statusIds));
  if (state.assigneeIds.length > 0) out.set('asg', encodeIntList(state.assigneeIds));
  if (state.categoryIds.length > 0) out.set('cat', encodeIntList(state.categoryIds));
  if (state.milestoneIds.length > 0) out.set('ms', encodeIntList(state.milestoneIds));
  if (state.priorityIds.length > 0) out.set('pr', encodeIntList(state.priorityIds));
  if (state.backlogProjectKeys.length > 0)
    out.set('bp', encodeStringList(state.backlogProjectKeys));
  if (state.keyword.trim().length > 0) out.set('q', state.keyword.trim());
  if (state.updatedSince !== '90d') out.set('us', state.updatedSince);
  return out;
}

export function decodeInboxFromParams(params: URLSearchParams): InboxUrlState {
  const raw = params.get('view') ?? 'pending';
  const filter = (VALID_FILTERS as readonly string[]).includes(raw)
    ? (raw as SuggestionFilter)
    : 'pending';
  const wbsItemId = params.get('wbs');
  const usRaw = params.get('us');
  const updatedSince: UpdatedSincePreset = (
    UPDATED_SINCE_PRESETS as readonly string[]
  ).includes(usRaw ?? '')
    ? (usRaw as UpdatedSincePreset)
    : '90d';
  return {
    filter,
    wbsItemId: wbsItemId && wbsItemId.length > 0 ? wbsItemId : null,
    statusIds: parseIntList(params.get('st')),
    assigneeIds: parseIntList(params.get('asg')),
    categoryIds: parseIntList(params.get('cat')),
    milestoneIds: parseIntList(params.get('ms')),
    priorityIds: parseIntList(params.get('pr')),
    backlogProjectKeys: parseStringList(params.get('bp')),
    keyword: params.get('q') ?? '',
    updatedSince,
  };
}

/** Callable wrapper ----------------------------------------------------- */

export type ReactAction = 'accept' | 'dismiss' | 'snooze' | 'revive';

/**
 * Realtime-search snapshot. The inbox now generates pending rows from
 * Backlog API on demand and never persists them to Firestore until the
 * user acts. When the user does act, we pass a snapshot back so the
 * callable can lazy-create the `backlogSuggestions` doc in the same
 * transaction. Field names mirror `BacklogSuggestionDoc`.
 */
export interface ReactOnSuggestionSnapshot {
  issueKey: string;
  backlogProjectKey: string;
  issueSummary: string;
  issueUrl: string;
  issueStatus: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
  issueTypeId?: number | null;
  issueTypeName?: string | null;
  categoryNames?: string[];
  milestoneNames?: string[];
  priorityId?: number | null;
  priorityName?: string | null;
  dueDate?: string | null;
  issueCreatedAt?: string | null;
  kind: SuggestionKind;
  candidates: SuggestionCandidate[];
  candidateWbsItemIds: string[];
}

export interface ReactOnSuggestionBody {
  projectId: string;
  suggestionId: string;
  action: ReactAction;
  /**
   * Caller-selected target WbsItem on accept. For `link_to_wbs` flows the
   * value must be one of `candidateWbsItemIds`; for `create_new_wbs_item`
   * it is the freshly-created WbsItem's ID.
   */
  wbsItemId?: string;
  snoozeHours?: number;
  reason?: string;
  /**
   * Optional realtime-search snapshot. When present and no persisted
   * doc exists, the server creates the doc from this snapshot before
   * applying the action. Ignored when a doc already exists.
   */
  snapshot?: ReactOnSuggestionSnapshot;
}

export interface ReactOnSuggestionResult {
  ok: true;
  suggestionId: string;
  wbsItemId?: string;
}

export type CallableRunner = (
  body: ReactOnSuggestionBody,
) => Promise<ReactOnSuggestionResult>;

let _runner: CallableRunner | null = null;

/** Swap the callable for tests; returns the previous runner. */
export function setCallableRunnerForTests(
  runner: CallableRunner | null,
): CallableRunner | null {
  const prev = _runner;
  _runner = runner;
  return prev;
}

function defaultRunner(): CallableRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<ReactOnSuggestionBody, ReactOnSuggestionResult>(
      fns,
      'reactOnSuggestion',
    );
    const res = await callable(body);
    return res.data;
  };
}

export async function reactOnSuggestion(
  body: ReactOnSuggestionBody,
): Promise<ReactOnSuggestionResult> {
  const run = _runner ?? defaultRunner();
  return await run(body);
}

/** Convenience helpers around the callable, matching the design doc. */
export const suggestionActions = {
  acceptExisting: (args: {
    projectId: string;
    suggestionId: string;
    wbsItemId: string;
    snapshot?: ReactOnSuggestionSnapshot;
  }) =>
    reactOnSuggestion({
      ...args,
      action: 'accept',
    }),
  acceptNewWbsItem: (args: {
    projectId: string;
    suggestionId: string;
    wbsItemId: string;
    snapshot?: ReactOnSuggestionSnapshot;
  }) =>
    reactOnSuggestion({
      ...args,
      action: 'accept',
    }),
  dismiss: (args: {
    projectId: string;
    suggestionId: string;
    reason?: string;
    snapshot?: ReactOnSuggestionSnapshot;
  }) =>
    reactOnSuggestion({
      ...args,
      action: 'dismiss',
    }),
  snooze: (args: {
    projectId: string;
    suggestionId: string;
    snoozeHours: number;
    reason?: string;
    snapshot?: ReactOnSuggestionSnapshot;
  }) =>
    reactOnSuggestion({
      ...args,
      action: 'snooze',
    }),
  revive: (args: { projectId: string; suggestionId: string }) =>
    reactOnSuggestion({
      ...args,
      action: 'revive',
    }),
};

/** Categorise a callable failure for banner copy. */
export type CallableErrorKind = 'permission' | 'timeout' | 'rules' | 'network';

export function classifyCallableError(err: unknown): CallableErrorKind {
  const msg = err instanceof Error ? err.message : String(err);
  const code =
    (err as { code?: string } | null)?.code ??
    (err as { details?: { code?: string } } | null)?.details?.code ??
    '';
  if (code.includes('failed-precondition') || /rules|ルール/i.test(msg))
    return 'rules';
  if (code.includes('permission') || /permission|denied/i.test(msg))
    return 'permission';
  if (/deadline|timeout|timed out/i.test(msg) || code.includes('deadline'))
    return 'timeout';
  if (
    /network|fetch|offline/i.test(msg) ||
    code.includes('unavailable') ||
    code.includes('internal')
  )
    return 'network';
  return 'network';
}

export const SNOOZE_OPTIONS: readonly { hours: number; label: string }[] = [
  { hours: 6, label: '6時間' },
  { hours: 24, label: '1日' },
  { hours: 72, label: '3日' },
  { hours: 168, label: '1週間' },
];
